import { Logger } from '@arshu/common/logger';
import { JsonValue, JsonValueType, toJsonValue } from './jsonValue.js';
import type { JsonArray } from './jsonArray.js';

export class JsonObject extends Map<string, JsonValue> {
    private _isReadOnly = false;
    private _entryIterator: IterableIterator<[string, JsonValue]> | null = null;
    // Case-insensitive key resolution to mirror csharp JsonObject's
    // OrderedDictionary<string,JsonValue>(StringComparer.OrdinalIgnoreCase): the underlying Map keeps
    // each key with its ORIGINAL case and insertion order (for iteration/serialization), while this
    // index (ordinal-folded key -> canonical stored key) routes get/has/set/delete case-insensitively.
    private _ciKeyIndex: Map<string, string> = new Map();

    // Ordinal (ASCII) case fold — matches StringComparer.OrdinalIgnoreCase for the ASCII identifier
    // keys used throughout the framework (and the asciiLower convention the other ports use), without
    // JS locale/Unicode special-casing.
    private static foldKey(key: string): string {
        let result = '';
        for (let i = 0; i < key.length; i++) {
            const c = key.charCodeAt(i);
            result += (c >= 65 && c <= 90) ? String.fromCharCode(c + 32) : key[i];
        }
        return result;
    }

    /** Case-insensitive presence check (overrides Map.has so all callers share OrdinalIgnoreCase semantics). */
    has(key: string): boolean {
        return this._ciKeyIndex.has(JsonObject.foldKey(key));
    }

    private removeCanonical(key: string): boolean {
        const fold = JsonObject.foldKey(key);
        const canonical = this._ciKeyIndex.get(fold);
        if (canonical === undefined) {
            return false;
        }
        this._ciKeyIndex.delete(fold);
        return super.delete(canonical);
    }

    constructor(data: Map<string, JsonValue> | Record<string, unknown> | null = null) {
        super();
        if (data) {
            if (data instanceof Map) {
                for (const [key, value] of data) {
                    this.set(key, value);
                }
            } else if (typeof data === 'object' && !Array.isArray(data)) {
                for (const [key, value] of Object.entries(data)) {
                    this.set(key, value);
                }
            }
        }
    }

    get isReadOnly(): boolean {
        return this._isReadOnly;
    }

    set isReadOnly(value: boolean) {
        this._isReadOnly = value;
        for (const [, childValue] of super.entries()) {
            JsonObject.applyReadOnlyToJsonValue(childValue, value);
        }
    }

    set(key: string, value: unknown): this {
        if (this._isReadOnly) {
            JsonObject.logReadOnlyWriteIgnored(`set key '${key}'`);
            return this;
        }
        const jsonValue = toJsonValue(value);
        JsonObject.applyReadOnlyToJsonValue(jsonValue, this._isReadOnly);
        // Case-insensitive: re-setting a case-variant updates the existing entry under its original
        // key (mirrors OrdinalIgnoreCase dict — first-inserted key wins); otherwise add + index it.
        const fold = JsonObject.foldKey(key);
        const canonical = this._ciKeyIndex.get(fold);
        if (canonical !== undefined) {
            super.set(canonical, jsonValue);
        } else {
            this._ciKeyIndex.set(fold, key);
            super.set(key, jsonValue);
        }
        return this;
    }

    get(key: string): JsonValue {
        const canonical = this._ciKeyIndex.get(JsonObject.foldKey(key));
        const value = canonical !== undefined ? super.get(canonical) : undefined;
        return value !== undefined ? value : JsonValue.null();
    }

    tryGetValue(key: string): { found: boolean; value: JsonValue } {
        const canonical = this._ciKeyIndex.get(JsonObject.foldKey(key));
        if (canonical !== undefined) {
            const value = super.get(canonical);
            if (value !== undefined) {
                return { found: true, value };
            }
        }
        return { found: false, value: JsonValue.null() };
    }

    tryAdd(key: string, value: unknown): boolean {
        if (this._isReadOnly) {
            JsonObject.logReadOnlyWriteIgnored(`try-add key '${key}'`);
            return false;
        }
        if (this.has(key)) {
            return false;
        }
        this.set(key, value);
        return true;
    }

    add(key: string, value: unknown): void {
        if (this._isReadOnly) {
            JsonObject.logReadOnlyWriteIgnored(`add key '${key}'`);
            return;
        }
        if (this.has(key)) {
            throw new Error(`An item with the same key has already been added. Key: ${key}`);
        }
        const jsonValue = toJsonValue(value);
        JsonObject.applyReadOnlyToJsonValue(jsonValue, this._isReadOnly);
        this._ciKeyIndex.set(JsonObject.foldKey(key), key);
        super.set(key, jsonValue);
    }

    remove(key: string): boolean {
        if (this._isReadOnly) {
            JsonObject.logReadOnlyWriteIgnored(`remove key '${key}'`);
            return false;
        }
        return this.removeCanonical(key);
    }

    delete(key: string): boolean {
        if (this._isReadOnly) {
            JsonObject.logReadOnlyWriteIgnored(`remove key '${key}'`);
            return false;
        }
        return this.removeCanonical(key);
    }

    clear(): void {
        if (this._isReadOnly) {
            JsonObject.logReadOnlyWriteIgnored('clear');
            return;
        }
        this._ciKeyIndex.clear();
        super.clear();
    }

    contains(item: [string, JsonValue]): boolean {
        const [key, value] = item;
        const canonical = this._ciKeyIndex.get(JsonObject.foldKey(key));
        return canonical !== undefined && super.get(canonical) === value;
    }

    private static logReadOnlyWriteIgnored(operation: string): void {
        const message = `JsonObject read-only write ignored: ${operation}`;
        Logger.warn(message, 'JsonObject');
    }

    static applyReadOnlyToJsonValue(value: JsonValue, readOnly: boolean): void {
        if (value.isObject) {
            const obj = value.asObject();
            if (obj) {
                obj.isReadOnly = readOnly;
            }
            return;
        }

        if (value.isArray) {
            const arr: JsonArray | null = value.asArray();
            if (arr) {
                arr.isReadOnly = readOnly;
            }
        }
    }

    static getAtIndex(obj: ReadonlyMap<string, JsonValue>, index: number): JsonValue {
        if (index < 0 || index >= obj.size) {
            throw new RangeError(`Index ${index} is outside the JsonObject bounds.`);
        }

        let currentIndex = 0;
        for (const value of obj.values()) {
            if (currentIndex++ === index) {
                return value;
            }
        }

        return JsonValue.null();
    }

    copyTo(target: JsonObject): void;
    copyTo(target: Array<[string, JsonValue]>, arrayIndex: number): void;
    copyTo(target: JsonObject | Array<[string, JsonValue]>, arrayIndex: number = 0): void {
        if (target instanceof JsonObject) {
            for (const [key, value] of super.entries()) {
                target.set(key, value);
            }
            return;
        }

        let writeIndex = arrayIndex;
        for (const entry of super.entries()) {
            target[writeIndex++] = entry;
        }
    }

    traversePath(path: string): JsonValue {
        if (!path) {
            return JsonValue.null();
        }

        let current: JsonValue = JsonValue.object(this);
        const parts = path.split('.');
        for (const part of parts) {
            if (!current.isObject) {
                return JsonValue.null();
            }

            const next = current.getObject().tryGetValue(part);
            if (!next.found) {
                return JsonValue.null();
            }
            current = next.value;
        }

        return current;
    }

    /**
     * Returns a structural deep copy of this object into a fresh mutable JsonObject.
     * Member order is preserved; nested objects/arrays are recursively cloned. No text
     * serialization. Replaces the historical serialize-then-parse clone round-trip.
     */
    deepClone(): JsonObject {
        const clone = new JsonObject();
        for (const [key, value] of super.entries()) {
            clone.set(key, value.deepClone());
        }
        return clone;
    }

    compare(toCompare: JsonObject): boolean {
        if (this.size !== toCompare.size) {
            return false;
        }

        for (const [key, value] of super.entries()) {
            if (!toCompare.has(key)) {
                return false;
            }
            const otherValue = toCompare.get(key);
            if (!value.equals(otherValue)) {
                return false;
            }
        }
        return true;
    }

    getValue(key: string): JsonValue {
        return this.get(key);
    }

    hasKey(key: string): boolean {
        return this.has(key);
    }

    containsKey(key: string): boolean {
        return this.has(key);
    }

    getString(key: string): string | null {
        const value = this.getValue(key);
        if (value.isNull) {
            return null;
        }
        return value.toString();
    }

    entries(): MapIterator<[string, JsonValue]> {
        let index = 0;
        const snapshot = Array.from(super.entries());
        return {
            next(): IteratorResult<[string, JsonValue]> {
                if (index >= snapshot.length) {
                    return { done: true, value: undefined };
                }

                const value = snapshot[index];
                index++;
                return { done: false, value };
            },
            [Symbol.dispose](): void {
                // no-op for iterator protocol compatibility
            },
            [Symbol.iterator](): MapIterator<[string, JsonValue]> {
                return this;
            }
        };
    }

    values(): MapIterator<JsonValue> {
        let index = 0;
        const snapshot = Array.from(super.values());
        return {
            next(): IteratorResult<JsonValue> {
                if (index >= snapshot.length) {
                    return { done: true, value: undefined };
                }

                const value = snapshot[index];
                index++;
                return { done: false, value };
            },
            [Symbol.dispose](): void {
                // no-op for iterator protocol compatibility
            },
            [Symbol.iterator](): MapIterator<JsonValue> {
                return this;
            }
        };
    }

    getEnumerator(): MapIterator<[string, JsonValue]> {
        let index = 0;
        const snapshot = Array.from(super.entries());
        return {
            next(): IteratorResult<[string, JsonValue]> {
                if (index >= snapshot.length) {
                    return { done: true, value: undefined };
                }

                const value = snapshot[index];
                index++;
                return { done: false, value };
            },
            [Symbol.dispose](): void {
                // no-op for iterator protocol compatibility
            },
            [Symbol.iterator](): MapIterator<[string, JsonValue]> {
                return this;
            }
        };
    }

    next(): IteratorResult<[string, JsonValue], undefined> {
        if (this._entryIterator === null) {
            this._entryIterator = super.entries();
        }

        const result = this._entryIterator.next();
        if (result.done) {
            this._entryIterator = null;
            return { done: true, value: undefined };
        }

        const [key, value] = result.value;
        return { done: false, value: [key, value] };
    }

    [Symbol.iterator](): MapIterator<[string, JsonValue]> {
        return super.entries();
    }

    get count(): number {
        return this.size;
    }

    static fromPlainObject(obj: Record<string, unknown> | null | undefined): JsonObject {
        if (obj === null || obj === undefined) {
            return new JsonObject();
        }

        if (obj instanceof JsonObject) {
            return obj as JsonObject;
        }

        if (typeof obj !== 'object' || Array.isArray(obj)) {
            return new JsonObject();
        }

        const jsonObject = new JsonObject();
        for (const [key, value] of Object.entries(obj)) {
            jsonObject.set(key, value);
        }
        return jsonObject;
    }

    toObject(): Record<string, unknown> {
        const obj: Record<string, unknown> = {};
        for (const [key, value] of this.entries()) {
            if (value instanceof JsonValue) {
                switch (value.typeName) {
                    case JsonValueType.String:
                        obj[key] = value.asString();
                        break;
                    case JsonValueType.Number:
                        obj[key] = value.asNumber();
                        break;
                    case JsonValueType.Integer:
                        obj[key] = value.asInteger();
                        break;
                    case JsonValueType.Bool:
                        obj[key] = value.asBool();
                        break;
                    case JsonValueType.Array: {
                        const arr: JsonArray | null = value.asArray();
                        if (arr && typeof arr.toArray === 'function') {
                            obj[key] = arr.toArray();
                        } else if (Array.isArray(arr)) {
                            obj[key] = arr;
                        } else {
                            obj[key] = [];
                        }
                        break;
                    }
                    case JsonValueType.Object: {
                        const innerObj = value.asObject();
                        if (innerObj && typeof innerObj.toObject === 'function') {
                            obj[key] = innerObj.toObject();
                        } else if (innerObj && typeof innerObj === 'object') {
                            obj[key] = innerObj;
                        } else {
                            obj[key] = {};
                        }
                        break;
                    }
                    case JsonValueType.Null:
                    default:
                        obj[key] = null;
                        break;
                }
            } else {
                obj[key] = value;
            }
        }
        return obj;
    }
}
